<script setup>
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { computed, ref } from 'vue';
import CrmAPI from 'dashboard/api/integrations/crm';
import { useAlert } from 'dashboard/composables';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SearchableDropdown from './SearchableDropdown.vue';

const props = defineProps({
  deal: {
    type: Object,
    required: true,
  },
  integrationId: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['stageUpdated', 'productsUpdated', 'assigneeUpdated']);

const CrmApiIntegration = new CrmAPI(props.integrationId);
const updatingStageId = ref(null);
const isEditingAssignee = ref(false);
const isLoadingUsers = ref(false);
const isUpdatingAssignee = ref(false);
const users = ref([]);
const selectedUserId = ref(null);

const canSaveAssignee = computed(
  () =>
    selectedUserId.value != null &&
    String(selectedUserId.value) !==
      String(props.deal.user?.id ?? props.deal.user_id) &&
    !isUpdatingAssignee.value
);

const editAssignee = async () => {
  isEditingAssignee.value = true;
  isLoadingUsers.value = true;
  selectedUserId.value = null;

  try {
    const response = await CrmApiIntegration.getUsers();
    users.value = response.data.users || [];
    selectedUserId.value =
      users.value.find(
        user =>
          String(user.id) === String(props.deal.user?.id ?? props.deal.user_id)
      )?.id ?? null;
  } catch (error) {
    useAlert('Erro ao carregar responsáveis');
    isEditingAssignee.value = false;
  } finally {
    isLoadingUsers.value = false;
  }
};

const updateAssignee = async () => {
  if (!canSaveAssignee.value) return;
  isUpdatingAssignee.value = true;

  try {
    await CrmApiIntegration.updateDealAssignee({
      deal_id: props.deal.id,
      user_id: selectedUserId.value,
    });
    useAlert('Responsável pela venda atualizado com sucesso');
    isEditingAssignee.value = false;
    emit('assigneeUpdated');
  } catch (error) {
    useAlert('Erro ao atualizar o responsável pela venda');
  } finally {
    isUpdatingAssignee.value = false;
  }
};
const isEditingProducts = ref(false);
const isLoadingProducts = ref(false);
const isAddingProduct = ref(false);
const removingProductId = ref(null);
const productCatalog = ref([]);
const selectedProduct = ref(null);
const productQuantity = ref(1);
const productPrice = ref(0);

const formattedDate = computed(() => {
  const { created_at } = props.deal;
  if (!created_at) return '';
  return format(new Date(created_at), "dd MMM. yyyy 'às' HH:mm", {
    locale: ptBR,
  });
});

const assignee = computed(() => {
  const user = props.deal.user;

  if (!user) return null;
  const { name, nickname } = user;

  return {
    name,
    nickname,
  };
});

const contact = computed(() => {
  return props.deal.contacts?.[0] || null;
});

const contactPhone = computed(() => {
  const phone = contact.value?.phones?.[0];
  return phone?.phone || phone?.value || '';
});

const leadName = computed(() => contact.value?.name || props.deal.name);

const pipelineName = computed(() => {
  return props.deal.deal_pipeline?.name || props.deal.pipeline?.name || '';
});

const customFields = computed(() => {
  return props.deal.deal_custom_fields || [];
});

const products = computed(() => {
  return props.deal.deal_products || [];
});

const productId = product => product.product_id ?? product.id;
const productName = product => product.name?.trim() || 'Produto sem nome';

const availableProducts = computed(() => {
  const linkedProductIds = new Set(
    products.value.map(product => String(productId(product)))
  );

  return productCatalog.value.filter(
    product => !linkedProductIds.has(String(product.id))
  );
});

const pipelineStages = computed(() => {
  return Object.values(props.deal.pipeline_stages || {});
});

const progressStages = computed(() =>
  pipelineStages.value.filter(stage => stage.code !== 'lost')
);

const wonStage = computed(() => {
  return pipelineStages.value.find(stage => stage.code === 'won') || null;
});

const lostStage = computed(() => {
  return pipelineStages.value.find(stage => stage.code === 'lost') || null;
});

const isSameStage = (firstStageId, secondStageId) => {
  if (firstStageId == null || secondStageId == null) return false;
  return String(firstStageId) === String(secondStageId);
};

const status = computed(() => {
  const dealStageId = props.deal.deal_stage?.id;
  if (!dealStageId) return null;

  const matchedStage = pipelineStages.value.find(
    stage => isSameStage(stage.id, dealStageId)
  );

  return matchedStage?.code || props.deal.deal_stage?.code || null;
});

const currentStage = computed(() => {
  const dealStageId = props.deal.deal_stage?.id;
  return (
    pipelineStages.value.find(stage => isSameStage(stage.id, dealStageId)) ||
    props.deal.deal_stage
  );
});

const currentStageIndex = computed(() =>
  progressStages.value.findIndex(stage =>
    isSameStage(stage.id, currentStage.value?.id)
  )
);

const statusLabel = computed(() => {
  if (status.value === 'won') return 'Ganho';
  if (status.value === 'lost') return 'Perdido';
  return currentStage.value?.name || 'Em andamento';
});

const statusMessage = computed(() => {
  if (status.value === 'won') return 'Negócio convertido';
  if (status.value === 'lost') return 'Negócio não convertido';
  return 'Em andamento';
});

const statusClasses = computed(() => {
  if (status.value === 'lost') return 'bg-red-50 text-red-700';
  if (status.value === 'won') return 'bg-green-50 text-green-700';
  return 'bg-woot-50 text-woot-700';
});

const statusIcon = computed(() => {
  if (status.value === 'won') return 'checkmark-circle';
  if (status.value === 'lost') return 'dismiss-circle';
  return 'status';
});

const dealUrl = 'https://crm.wellon.digital/admin/leads/view/' + props.deal.id;

const primaryProduct = computed(() => products.value[0] || null);

const parseAmount = value => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== 'string' || !value.trim()) return null;

  const sanitizedValue = value.replace(/[^\d,.-]/g, '');
  if (!/\d/.test(sanitizedValue)) return null;

  const normalizedValue = sanitizedValue.includes(',')
    ? sanitizedValue.replace(/\./g, '').replace(',', '.')
    : sanitizedValue;
  const parsedValue = Number(normalizedValue);

  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const getProductQuantity = product => {
  const candidates = [product.amount, product.quantity]
    .map(parseAmount)
    .filter(value => value !== null);
  const price = parseAmount(product.price ?? product.base_price);
  const total = parseAmount(product.total);

  if (price && total !== null) {
    const matchingQuantity = candidates.find(
      quantity => Math.abs(price * quantity - total) < 0.01
    );
    if (matchingQuantity !== undefined) return matchingQuantity;

    const calculatedQuantity = total / price;
    if (Number.isFinite(calculatedQuantity)) return calculatedQuantity;
  }

  return candidates[0] ?? 1;
};

const formatProductQuantity = product =>
  new Intl.NumberFormat('pt-BR', {
    maximumFractionDigits: 4,
  }).format(getProductQuantity(product));

const formatProductAmount = product => {
  const total = parseAmount(product.total);
  if (total !== null) return total;

  const price = parseAmount(product.price ?? product.base_price) ?? 0;
  return price * getProductQuantity(product);
};

const productAmount = computed(() =>
  products.value.reduce(
    (total, product) => total + formatProductAmount(product),
    0
  )
);

const formattedProductAmount = computed(() =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(productAmount.value)
);

const formatCurrency = value =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);

const isCurrentProgressStage = index => {
  return status.value !== 'lost' && index === currentStageIndex.value;
};

const isUpdatingStage = stageId => {
  return isSameStage(stageId, updatingStageId.value);
};

const isUpdatingProgressStage = index => {
  return isUpdatingStage(progressStages.value[index]?.id);
};

const stageLabelClasses = index => {
  if (isUpdatingProgressStage(index)) return 'text-woot-700';
  if (!isCurrentProgressStage(index)) return 'text-ash-600';
  return status.value === 'won' ? 'text-green-700' : 'text-woot-700';
};

const stageIndicatorClasses = index => {
  if (isUpdatingProgressStage(index)) {
    return 'border-woot-600 bg-woot-600 text-white dark:bg-woot-600';
  }

  if (!isCurrentProgressStage(index)) {
    return 'border-ash-300 bg-white dark:bg-slate-800';
  }

  if (status.value === 'won') {
    return 'border-green-600 bg-green-600 text-white dark:bg-green-600';
  }

  return 'border-woot-600 bg-woot-600 text-white dark:bg-woot-600';
};

const openDeal = () => {
  if (dealUrl) window.open(dealUrl, '_blank', 'noopener,noreferrer');
};

const loadProducts = async () => {
  if (productCatalog.value.length || isLoadingProducts.value) return;

  isLoadingProducts.value = true;

  try {
    const allProducts = [];
    let page = 1;
    let hasMore = true;

    while (hasMore && page <= 100) {
      // eslint-disable-next-line no-await-in-loop
      const response = await CrmApiIntegration.getProducts(page, 200);
      const data = response.data;
      allProducts.push(...(data.products || []));
      hasMore = Boolean(data.has_more);
      page += 1;
    }

    productCatalog.value = allProducts.map(product => ({
      id: product.id,
      name: product.name,
      price: parseAmount(product.base_price) ?? 0,
    }));
  } catch (error) {
    console.error('Error loading products:', error);
    useAlert('Erro ao carregar produtos');
  } finally {
    isLoadingProducts.value = false;
  }
};

const toggleProductEditor = () => {
  isEditingProducts.value = !isEditingProducts.value;
  if (isEditingProducts.value) loadProducts();
};

const selectProduct = product => {
  selectedProduct.value = product;
  productQuantity.value = 1;
  productPrice.value = product.price;
};

const resetProductForm = () => {
  selectedProduct.value = null;
  productQuantity.value = 1;
  productPrice.value = 0;
};

const addProduct = async () => {
  const quantity = Number(productQuantity.value);
  const price = Number(productPrice.value);

  if (
    !selectedProduct.value ||
    !Number.isFinite(quantity) ||
    quantity < 1 ||
    !Number.isFinite(price) ||
    price < 0
  ) {
    useAlert('Informe um produto, uma quantidade e um preço válidos');
    return;
  }

  isAddingProduct.value = true;

  try {
    await CrmApiIntegration.addDealProduct({
      deal_id: props.deal.id,
      product_id: selectedProduct.value.id,
      quantity,
      price,
    });
    useAlert('Produto adicionado com sucesso');
    resetProductForm();
    emit('productsUpdated');
  } catch (error) {
    console.error('Error adding deal product:', error);
    useAlert('Erro ao adicionar produto');
  } finally {
    isAddingProduct.value = false;
  }
};

const removeProduct = async product => {
  const id = productId(product);
  if (id == null || removingProductId.value !== null) return;

  removingProductId.value = id;

  try {
    await CrmApiIntegration.removeDealProduct({
      deal_id: props.deal.id,
      product_id: id,
    });
    useAlert('Produto removido com sucesso');
    emit('productsUpdated');
  } catch (error) {
    console.error('Error removing deal product:', error);
    useAlert('Erro ao remover produto');
  } finally {
    removingProductId.value = null;
  }
};

const updateStage = async stageId => {
  if (!stageId || updatingStageId.value !== null) return;

  updatingStageId.value = stageId;

  try {
    const response = await CrmApiIntegration.updateDealStage({
      deal_id: props.deal.id,
      stage_id: stageId,
    });

    if (response.data) {
      useAlert('Estágio da negociação atualizado com sucesso');
      emit('stageUpdated');
    }
  } catch (error) {
    console.error('Error updating deal stage:', error);
    useAlert('Erro ao atualizar o estágio da negociação');
  } finally {
    updatingStageId.value = null;
  }
};
</script>

<template>
  <div
    class="z-50 flex max-h-[32rem] flex-col gap-4 overflow-auto rounded-2xl border border-solid border-ash-200 bg-white p-5 text-left shadow-xl dark:bg-slate-800"
  >
    <div class="flex items-center gap-3">
      <div
        class="flex size-11 shrink-0 items-center justify-center rounded-full bg-purple-50 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300"
      >
        <fluent-icon icon="person" size="24" />
      </div>
      <div class="min-w-0 flex-1">
        <span class="block text-xs text-ash-600">Lead</span>
        <strong class="block truncate text-lg text-ash-900">#{{ deal.id }}</strong>
      </div>
    </div>

    <div>
      <h3 class="m-0 break-words text-xl font-semibold leading-tight text-ash-900">
        {{ leadName }}
      </h3>
      <div v-if="contactPhone" class="mt-2 flex items-center gap-2 text-ash-600">
        <fluent-icon icon="call" size="17" />
        <span class="text-sm">{{ contactPhone }}</span>
      </div>
      <div v-if="pipelineName" class="mt-2 flex items-center gap-2 text-ash-600">
        <fluent-icon icon="status" size="17" />
        <span class="text-xs">Funil:</span>
        <strong class="truncate text-xs font-medium text-ash-900">
          {{ pipelineName }}
        </strong>
      </div>
    </div>

    <div class="flex flex-wrap items-center gap-2">
      <div
        class="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold uppercase"
        :class="statusClasses"
      >
        <fluent-icon :icon="statusIcon" size="16" />
        {{ statusLabel }}
      </div>
      <span
        class="inline-flex items-center rounded-lg px-3 py-2 text-[10px] font-medium"
        :class="statusClasses"
      >
        {{ statusMessage }}
      </span>
    </div>

    <div v-if="progressStages.length" class="flex w-full pt-1">
      <div
        v-for="(stage, index) in progressStages"
        :key="stage.id"
        class="relative flex min-w-0 flex-1"
      >
        <button
          type="button"
          class="flex min-w-0 flex-1 cursor-pointer flex-col items-center gap-2 rounded-md bg-transparent p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-woot-500 disabled:cursor-wait"
          :aria-label="`Alterar estágio para ${stage.name}`"
          :disabled="updatingStageId !== null"
          @click="updateStage(stage.id)"
        >
          <span
            class="w-full truncate px-1 text-center text-[10px] font-medium"
            :class="stageLabelClasses(index)"
          >
            {{ stage.name }}
          </span>
          <div class="relative flex w-full items-center justify-center">
            <span
              v-if="index > 0"
              class="absolute right-1/2 h-px w-1/2 border-t border-dashed border-ash-300"
            />
            <span
              v-if="index < progressStages.length - 1"
              class="absolute left-1/2 h-px w-1/2 border-t border-dashed border-ash-300"
            />
            <span
              class="relative z-10 flex size-5 items-center justify-center rounded-full border-2 transition-transform hover:scale-110"
              :class="stageIndicatorClasses(index)"
            >
              <Spinner
                v-if="isUpdatingStage(stage.id)"
                :size="12"
                class="text-white"
              />
              <fluent-icon
                v-else-if="isCurrentProgressStage(index) && status === 'won'"
                icon="checkmark"
                size="12"
              />
              <span
                v-else-if="isCurrentProgressStage(index)"
                class="size-1 rounded-full bg-white"
              />
            </span>
          </div>
        </button>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
      <div
        v-if="assignee || props.integrationId === 'welloncrm'"
        class="flex min-w-0 items-center gap-3 rounded-xl border border-ash-200 p-3"
      >
        <div
          class="flex size-9 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
        >
          <fluent-icon icon="person" size="20" />
        </div>
        <div class="min-w-0 flex-1">
          <span class="block text-xs text-ash-600">Responsável</span>
          <strong class="block truncate text-sm text-ash-900">
            {{ assignee?.name || assignee?.nickname || 'Sem responsável' }}
          </strong>
        </div>
        <button
          v-if="props.integrationId === 'welloncrm'"
          type="button"
          aria-label="Editar responsável"
          class="rounded-md bg-transparent p-1 text-purple-700 hover:bg-purple-50 disabled:opacity-60 dark:text-purple-300"
          :disabled="isEditingAssignee"
          @click="editAssignee"
        >
          <fluent-icon icon="edit" size="16" />
        </button>
      </div>
      <div class="flex min-w-0 items-center gap-3 rounded-xl border border-ash-200 p-3">
        <div
          class="flex size-9 shrink-0 items-center justify-center rounded-full bg-purple-50 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300"
        >
          <fluent-icon icon="briefcase" size="20" />
        </div>
        <div class="min-w-0 flex-1">
          <span class="block text-xs text-ash-600">Produtos</span>
          <strong class="block truncate text-sm text-ash-900">
            {{ products.length || 'Nenhum' }}
          </strong>
          <strong v-if="primaryProduct" class="mt-1 block text-xs text-ash-900">
            Total: {{ formattedProductAmount }}
          </strong>
        </div>
      </div>
    </div>

    <section v-if="isEditingAssignee" class="border-t border-ash-200 pt-4">
      <Spinner v-if="isLoadingUsers" :size="16" class="text-purple-700" />
      <template v-else>
        <SearchableDropdown
          v-if="!isUpdatingAssignee"
          :value="selectedUserId"
          label="Responsável pela venda"
          type="responsavel"
          placeholder="Selecione um responsável"
          :items="users"
          @change="selectedUserId = $event.id"
        />
        <div class="mt-3 flex justify-end gap-2">
          <button
            type="button"
            class="rounded-md bg-transparent px-3 py-2 text-xs text-ash-600 disabled:opacity-60"
            :disabled="isUpdatingAssignee"
            @click="isEditingAssignee = false"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="flex items-center gap-2 rounded-md bg-purple-700 px-3 py-2 text-xs font-medium text-white hover:bg-purple-800 disabled:opacity-60"
            :disabled="!canSaveAssignee"
            @click="updateAssignee"
          >
            <Spinner v-if="isUpdatingAssignee" :size="14" class="text-white" />
            Salvar responsável
          </button>
        </div>
      </template>
    </section>

    <section v-if="props.integrationId === 'welloncrm'" class="border-t border-ash-200 pt-4">
      <div class="mb-3 flex items-center justify-between gap-3">
        <h4 class="m-0 text-sm font-semibold text-ash-900">Produtos</h4>
        <button
          type="button"
          class="flex items-center gap-1 rounded-md bg-transparent px-2 py-1 text-xs font-medium text-purple-700 hover:bg-purple-50 dark:text-purple-300 dark:hover:bg-purple-900/30"
          @click="toggleProductEditor"
        >
          <fluent-icon :icon="isEditingProducts ? 'dismiss' : 'edit'" size="14" />
          {{ isEditingProducts ? 'Fechar' : 'Editar' }}
        </button>
      </div>

      <p v-if="!products.length" class="m-0 text-xs text-ash-600">
        Nenhum produto adicionado.
      </p>
      <div v-else class="flex flex-col gap-2">
        <div
          v-for="product in products"
          :key="productId(product)"
          class="flex items-center gap-3 rounded-lg bg-ash-50 px-3 py-2 dark:bg-slate-900"
        >
          <div class="min-w-0 flex-1">
            <strong class="block truncate text-xs text-ash-900">
              {{ productName(product) }}
            </strong>
            <span class="text-[11px] text-ash-600">
              {{ formatProductQuantity(product) }}x ·
              {{ formatCurrency(formatProductAmount(product)) }}
            </span>
          </div>
          <button
            v-if="isEditingProducts"
            type="button"
            class="flex size-8 shrink-0 items-center justify-center rounded-md bg-transparent text-red-600 hover:bg-red-50 disabled:cursor-wait disabled:opacity-60 dark:hover:bg-red-900/30"
            :aria-label="`Remover ${productName(product)}`"
            :title="`Remover ${productName(product)}`"
            :disabled="removingProductId !== null"
            @click="removeProduct(product)"
          >
            <Spinner
              v-if="String(removingProductId) === String(productId(product))"
              :size="14"
            />
            <fluent-icon v-else icon="delete" size="16" />
          </button>
        </div>
      </div>

      <div v-if="isEditingProducts" class="mt-4 border-t border-ash-200 pt-4">
        <Spinner v-if="isLoadingProducts" :size="16" class="text-purple-700" />
        <template v-else>
          <SearchableDropdown
            :value="selectedProduct?.id"
            label="Adicionar produto"
            type="produtos"
            placeholder="Selecione um produto"
            :items="availableProducts"
            @change="selectProduct"
          />
          <div v-if="selectedProduct" class="mt-3 grid grid-cols-2 gap-3">
            <label class="text-xs text-ash-600">
              Quantidade
              <input
                v-model.number="productQuantity"
                type="number"
                min="1"
                step="1"
                class="mt-1 h-9 w-full rounded-md border border-ash-300 bg-white px-2 text-sm text-ash-900 dark:bg-slate-900"
              />
            </label>
            <label class="text-xs text-ash-600">
              Preço (R$)
              <input
                v-model.number="productPrice"
                type="number"
                min="0"
                step="0.01"
                class="mt-1 h-9 w-full rounded-md border border-ash-300 bg-white px-2 text-sm text-ash-900 dark:bg-slate-900"
              />
            </label>
          </div>
          <button
            v-if="selectedProduct"
            type="button"
            class="mt-3 flex w-full items-center justify-center gap-2 rounded-md bg-purple-700 px-3 py-2 text-xs font-medium text-white hover:bg-purple-800 disabled:cursor-wait disabled:opacity-70"
            :disabled="isAddingProduct"
            @click="addProduct"
          >
            <Spinner v-if="isAddingProduct" :size="14" class="text-white" />
            <fluent-icon v-else icon="add" size="15" />
            Adicionar produto
          </button>
        </template>
      </div>
    </section>

    <div class="flex flex-wrap items-center justify-between gap-3">
      <div v-if="customFields.length" class="flex flex-wrap gap-2">
        <div
          v-for="field in customFields.slice(0, 3)"
          :key="field.custom_field_id"
          class="inline-flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-800 dark:bg-blue-900/40 dark:text-blue-200"
        >
          <fluent-icon icon="tag" size="15" />
          <span class="font-medium">
            {{
              Array.isArray(field.value) ? field.value.join(', ') : field.value
            }}
          </span>
        </div>
      </div>
      <div v-if="formattedDate" class="ml-auto flex items-center gap-2 text-ash-600">
        <fluent-icon icon="calendar" size="18" />
        <div class="text-xs">
          <span class="block">Criado em</span>
          <strong class="font-medium text-ash-900">{{ formattedDate }}</strong>
        </div>
      </div>
    </div>

    <div
      v-if="props.integrationId === 'welloncrm'"
      class="flex flex-wrap items-center justify-between gap-2 border-t border-ash-200 pt-4"
    >
      <button
        v-if="status !== 'lost'"
        class="flex items-center gap-2 rounded-lg border border-red-500 bg-transparent px-3 py-2 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 disabled:cursor-wait disabled:opacity-70 dark:hover:bg-red-900/30"
        :disabled="!lostStage || updatingStageId !== null"
        @click="updateStage(lostStage?.id)"
      >
        <Spinner
          v-if="isUpdatingStage(lostStage?.id)"
          :size="15"
          class="text-red-600"
        />
        <fluent-icon v-else icon="dismiss" size="15" />
        Marcar como perdido
      </button>
      <div class="ml-auto flex items-center gap-2">
        <button
          v-if="dealUrl"
          class="flex items-center gap-2 rounded-lg bg-purple-700 px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-purple-800"
          @click="openDeal"
        >
          <fluent-icon icon="arrow-right" size="15" />
          Visualizar lead
        </button>
      </div>
    </div>
  </div>
</template>
